<template>
  <v-container class="accordion-container pl-0 pr-0" :class="variant">
    <v-expansion-panels class="accordion" :value="openIndex">
      <v-expansion-panel
        v-for="(item, index) in accordionData"
        :class="{ 'nav-drawer-accordion': isNavDrawer }"
      >
        <v-expansion-panel-header
          v-if="html"
          :key="index"
          class="accordion-header"
        >
          <div v-html="item.title"></div>
        </v-expansion-panel-header>
        <v-expansion-panel-header v-else class="accordion-header">
          {{ item.title }}
        </v-expansion-panel-header>
        <v-expansion-panel-content class="accordion-content">
          <div
            v-if="html"
            v-html="item.content"
            class="panel-inner-content"
          ></div>
          <div v-else class="panel-inner-content">
            {{ item.content }}
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-container>
</template>

<script>
  export default {
    name: 'Accordion',
    props: {
      html: {
        required: false,
        default: false,
        type: Boolean,
      },
      accordionData: {
        required: false,
        default: () => [],
        type: Array,
      },
      isNavDrawer: {
        required: false,
        default: false,
        type: Boolean,
      },
      // Optional style variant class, e.g. "adviser-faqs" (OMW-419).
      variant: {
        required: false,
        default: '',
        type: String,
      },
      // Index of the panel to open initially; null keeps every panel closed.
      openIndex: {
        required: false,
        default: null,
        type: Number,
      },
    },
  };
</script>

<style lang="scss">
  .accordion-container {
    max-width: 766px;
    padding-top: 0;
    padding-bottom: 40px;

    .nav-drawer-accordion {
      padding: 12px !important;
    }

    .accordion {
      .v-expansion-panel--next-active,
      .v-expansion-panel-header--active {
        border-bottom: 1px solid #e0e0e0 !important;
      }
      .v-expansion-panel-content__wrap {
        padding: 25px 33px;
      }
      .v-expansion-panel-header__icon {
        .v-icon {
          color: #282828 !important;
        }
      }
      .v-expansion-panel,
      .v-expansion-panel-header,
      .v-expansion-panel-content {
        margin: 0;

        .accordion-header {
          min-height: 70px;
          display: flex;
          align-items: center;
          font-size: 16px;
          font-weight: 700;
          line-height: 22px;
          h2 {
            font-size: 16px;
            font-weight: 700;
            line-height: 22px;
            text-align: left;
            color: #282828;
            padding-right: 24px;
          }
        }
        .accordion-content {
          background-color: #f9f9f9;
          display: flex;
          align-items: center;
          font-size: 14px;
          font-weight: 400;
          line-height: 20px;
          letter-spacing: 0.25px;
          color: #747474;
          .panel-inner-content {
            p {
              font-size: 14px;
              font-weight: 400;
              line-height: 20px;
              letter-spacing: 0.25px;
              text-align: left;
            }
            h2 {
              margin-bottom: 11px;
              font-size: 16px;
              font-weight: 600;
            }
            li {
              margin-bottom: 1em;
            }
            .accordion-image {
              width: 100%;
              margin: 12px 0;
            }
          }
        }
      }

      .v-expansion-panel-content {
        transition: max-height 5s ease, opacity 5s ease;
        overflow: hidden;
      }
    }
  }

  .custom-expansion-panel {
    border-bottom: 1px solid #e0e0e0;
  }

  .custom-expansion-panel
    .custom-expansion-panel
    .v-expansion-panel-content__wrap {
    border-top: 1px solid #e0e0e0;
  }
  // OMW-419 adviser FAQ accordion (spec 4.7 / 4.8). Only applies under the
  // "adviser-faqs" variant; the public /faqs page is untouched.
  .accordion-container.adviser-faqs {
    max-width: none;
    padding-bottom: 0;

    .accordion {
      .v-expansion-panel {
        margin: 0 !important;
        background: #ffffff;
        border: 1px solid #cccccc;
        border-top-width: 0;
        border-radius: 0;
        &::before {
          box-shadow: none;
        }
        &::after {
          border-top: none !important;
        }
        &:first-child {
          border-top-width: 1px;
          border-radius: 20px 20px 0 0;
        }
        &:last-child {
          border-radius: 0 0 20px 20px;
        }
        // Vuetify rounds an active panel on its own and the white answer
        // block has square corners, so clip children to the card radius or
        // an expanded last question pokes out of the bottom corners.
        &,
        &.v-expansion-panel--active {
          overflow: hidden;
          border-radius: 0;
        }
        &:first-child,
        &:first-child.v-expansion-panel--active {
          border-radius: 20px 20px 0 0;
        }
        &:last-child,
        &:last-child.v-expansion-panel--active {
          border-radius: 0 0 20px 20px;
        }
        &:first-child:last-child {
          border-radius: 20px;
        }
      }
      // Expanded question (Figma accordion.web "Top" frame): the title keeps
      // 24px side/top padding, the divider sits 12px under it and is inset
      // 24px on both sides rather than running edge to edge; the answer
      // starts 16px below the divider (content wrap padding).
      .v-expansion-panel--active > .v-expansion-panel-header,
      .v-expansion-panel-header--active {
        min-height: 60px;
        padding-bottom: 12px;
        border-bottom: none !important;
        &::after {
          content: '';
          position: absolute;
          left: 24px;
          right: 24px;
          bottom: 0;
          height: 1px;
          background: #cccccc;
        }
      }
      .v-expansion-panel-header__icon .v-icon {
        color: #306e2b !important;
      }
      .v-expansion-panel,
      .v-expansion-panel-header,
      .v-expansion-panel-content {
        .accordion-header {
          min-height: 72px;
          padding: 24px;
          font-family: 'Montserrat', sans-serif;
          font-weight: 600;
          font-size: 16px;
          line-height: 24px;
          color: #000000;
          h2 {
            font-weight: 600;
            font-size: 16px;
            line-height: 24px;
            color: #000000;
          }
        }
        .accordion-content {
          background-color: #ffffff;
          color: #666666;
          font-family: 'Montserrat', sans-serif;
          font-size: 14px;
          line-height: 24px;
          letter-spacing: 0;
          .v-expansion-panel-content__wrap {
            padding: 16px 24px 24px;
          }
          .panel-inner-content {
            width: 100%;
            p {
              font-size: 14px;
              line-height: 24px;
              letter-spacing: 0;
              color: #666666;
              margin: 0 0 12px 0;
            }
            ol,
            ul {
              padding-left: 20px;
              margin: 0 0 12px 0;
              li {
                margin-bottom: 4px;
              }
            }
            strong {
              color: #000000;
            }
            // global.scss sets `a` and `ul li` to 16px; answers are 14/24.
            li,
            a {
              font-size: inherit;
              line-height: inherit;
            }
            a {
              color: #306e2b;
            }
            // Trust-vs-Will comparison, one mini table per row label.
            .comparison-table {
              margin-top: 16px;
              .comparison-heading {
                font-weight: 600;
                font-size: 16px;
                line-height: 24px;
                color: #000000;
                padding: 16px 0;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                table-layout: fixed;
                th,
                td {
                  width: 50%;
                  border: 1px solid #cccccc;
                  padding: 20px 16px;
                  font-size: 14px;
                  line-height: 20px;
                  color: #000000;
                  vertical-align: top;
                  text-align: left;
                }
                th {
                  background: #ececec;
                  padding: 18px 16px;
                  font-weight: 600;
                }
              }
            }
          }
        }
      }
    }
  }
</style>
